import {
    Alert,
    AlertProps,
    Badge,
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    Snackbar,
    Tooltip,
    Typography,
} from "@mui/material";
import { useContext, useMemo, useState } from "react";
import LiveDowntimeReport from "./LiveDowntimeReport";
import SummaryReport from "./SummaryReport";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useStyles } from "./MillDashboard.styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { useTranslation } from "react-i18next";
import { SecondaryCodeRequirements } from "./SecondaryCodeRequirements";
import useRolesPermissions from "hooks/useRolesPermissions";
import { ShiftDropdown, ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import SummaryReportDashboard from "./SummaryReportDashboard/SummaryReportDashboard";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { FiltersButton } from "pages/Corporate/components/FiltersButton/FiltersButton.view";
import PrintIcon from "@mui/icons-material/Print";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { FullScreenButton } from "pages/Corporate/components/FullScreenButton/FullScreenButton.view";
import { MillDetailsReportContext } from "contexts/MillDetailsReportContext/MillDetailsReportContext";
import { DetailsReport } from "./DetailsReport/DetailsReport.view";
import DateRange from "components/DateRange/DateRange.view";
import useSummaryReportDashboardFilters, {
    initialSummaryReportDashboardFilters,
    validMillSummaryDefaultFields,
} from "./SummaryReportDashboard/components/SummaryReportDashboardFilters/useSummaryReportDashboardFilters";
import TrendReport from "./TrendReport";
import { useNavigate, useParams } from "react-router-dom";
import { DetailsReportEditionProvider } from "contexts/DetailsReportEditionContext/DetailsReportEditionProvider";
import { getEventReportDetailsExcelFile } from "api/queries/millDashboardAPI";
import { allElements, CORPORATE_BACKOFFICE_PATHS, maxExcelGenerationRange } from "constants/constants";
import { getEndOfDateRange, getStartOfDateRange, objectComparator } from "utilities/utilities";
import moment from "moment";
import useDateFormat from "hooks/useDateFormat";
import PermScanWifiIcon from "@mui/icons-material/PermScanWifi";
import { MillLocalserverLiveStatusContext } from "contexts/MillLocalserverLiveStatusContext/MillLocalserverLiveStatusContext";
import {
    initialDetailsReportFilters,
    validMillDetailsReportDefaultFields,
} from "./DetailsReport/DetailsReportFilters/useDetailsReportFilters";

enum ReportEnum {
    liveDowntimeReport,
    summaryReport, // <-- TODO: this needs to be removed
    detailsReport,
    summaryReportDashboard,
    secondaryCodeRequirements,
    trendReport,
}

const MillDashboard = () => {
    const classes = useStyles();
    const [report, setReport] = useState(ReportEnum.liveDowntimeReport);
    const [isLoadingExcel, setIsLoadingExcel] = useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertProps["severity"]>("info");
    const { isFullscreenActive, handleSetIsFullscreenActive } = useContext(CorporateContext);
    const {
        showDetails,
        showOrHideDetails,
        showOnlyPending,
        showOrHideOnlyPending,
        topHeaderArea,
        reportRangeStartDate,
        reportRangeEndDate,
        handleSetReportRangeEndDate,
        handleSetReportRangeStartDate,
        displayUnit,
        handleChangeDisplayUnit,
        pendingEventReportsCount,
    } = useContext(MillDashboardContext);
    const {
        showFilters: showSummaryReportFilters,
        handleShowFiltersChange: handleShowSummaryReportFilters,
        filters: summaryFilters,
    } = useContext(SummaryReportDashboardContext);
    const { displayInOptions } = useSummaryReportDashboardFilters();
    const {
        showFilters: showDetailsFilters,
        handleShowFiltersChange: handleShowDetailsFilters,
        filters,
    } = useContext(MillDetailsReportContext);
    const { isDisconnected } = useContext(MillLocalserverLiveStatusContext);

    const { getDateFormatByLanguage } = useDateFormat();

    const [seletedDateRange, setSelectedDateRange] = useState<{ startDate: Date; endDate: Date }>({
        startDate: reportRangeStartDate,
        endDate: reportRangeEndDate,
    });

    const { t, i18n } = useTranslation();
    const { shift, handleShift } = useContext(HMIContext);
    const { millId } = useParams();
    const navigate = useNavigate();

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    const showReportDateRange = useMemo(
        () =>
            Boolean(
                report === ReportEnum.summaryReportDashboard ||
                report === ReportEnum.detailsReport ||
                report === ReportEnum.trendReport,
            ),
        [report],
    );

    const reportsOptions = useMemo(
        () => [
            ...(process.env.REACT_APP_SHOW_SUMMARY_REPORT_DASHBOARD?.toLocaleLowerCase() === "true"
                ? [
                      {
                          id: ReportEnum.summaryReportDashboard,
                          label: t("locations.mill_dashboards.summary_report_dashboard"),
                      },
                  ]
                : []),
            ...(!(process.env.REACT_APP_SHOW_SUMMARY_REPORT_DASHBOARD?.toLocaleLowerCase() === "true")
                ? [
                      {
                          id: ReportEnum.summaryReport,
                          label: t("locations.mill_dashboards.summary_report"),
                      },
                  ]
                : []),
            {
                id: ReportEnum.liveDowntimeReport,
                label: t("locations.mill_dashboards.live_downtime_report.title"),
            },
            ...(process.env.REACT_APP_SHOW_TREND_REPORT_DASHBOARD?.toLocaleLowerCase() === "true"
                ? [
                      {
                          id: ReportEnum.trendReport,
                          label: t("locations.mill_dashboards.trend_report"),
                      },
                  ]
                : []),
            ...(process.env.REACT_APP_SHOW_SUMMARY_REPORT_DASHBOARD?.toLocaleLowerCase() === "true"
                ? [
                      {
                          id: ReportEnum.detailsReport,
                          label: t("locations.mill_dashboards.details.title"),
                      },
                  ]
                : []),

            ...(CORPORATE_PERMISSIONS?.corporationPermissions?.showSecondaryCodeRequirements
                ? [
                      {
                          id: ReportEnum.secondaryCodeRequirements,
                          label: t("locations.mill_dashboards.secondary_code_requirements"),
                      },
                  ]
                : []),
        ],
        [t, CORPORATE_PERMISSIONS],
    );

    const menuOptionComponents = useMemo(
        () =>
            reportsOptions.map((option) => {
                if (
                    option.id === ReportEnum.secondaryCodeRequirements &&
                    CORPORATE_PERMISSIONS?.corporationPermissions?.showSecondaryCodeRequirements
                ) {
                    return (
                        <MenuItem key={option.id} value={option.id} className={classes.menuItem}>
                            {option.label}
                            <Badge badgeContent={pendingEventReportsCount} color="error" className={classes.badge} />
                        </MenuItem>
                    );
                }
                return (
                    <MenuItem key={option.id} value={option.id} className={classes.menuItem}>
                        {option.label}
                    </MenuItem>
                );
            }),
        [
            reportsOptions,
            classes.menuItem,
            classes.badge,
            pendingEventReportsCount,
            CORPORATE_PERMISSIONS?.corporationPermissions?.showSecondaryCodeRequirements,
        ],
    );

    const reportsComponents = {
        [ReportEnum.liveDowntimeReport]: {
            component: <LiveDowntimeReport />,
        },
        [ReportEnum.summaryReport]: {
            component: <SummaryReport />,
        },
        [ReportEnum.detailsReport]: {
            component: (
                <DetailsReportEditionProvider>
                    <DetailsReport />
                </DetailsReportEditionProvider>
            ),
        },
        [ReportEnum.summaryReportDashboard]: {
            component: <SummaryReportDashboard />,
        },
        [ReportEnum.trendReport]: {
            component: <TrendReport />,
        },
        [ReportEnum.secondaryCodeRequirements]: {
            component: <SecondaryCodeRequirements />,
        },
    };

    const showFiltersBySelectedReport = useMemo(
        () => ({
            [ReportEnum.summaryReport]: showSummaryReportFilters,
            [ReportEnum.detailsReport]: showDetailsFilters,
            [ReportEnum.summaryReportDashboard]: showSummaryReportFilters,
        }),
        [showDetailsFilters, showSummaryReportFilters],
    );

    const handleFiltersChangeBySelectedReport = useMemo(
        () => ({
            [ReportEnum.summaryReport]: handleShowSummaryReportFilters,
            [ReportEnum.detailsReport]: handleShowDetailsFilters,
            [ReportEnum.summaryReportDashboard]: handleShowSummaryReportFilters,
        }),
        [handleShowDetailsFilters, handleShowSummaryReportFilters],
    );

    const showFiltersButton = useMemo(
        () =>
            Boolean(report === ReportEnum.summaryReport) ||
            Boolean(report === ReportEnum.summaryReportDashboard) ||
            (Boolean(report === ReportEnum.detailsReport) && !isFullscreenActive),
        [report, isFullscreenActive],
    );

    const showFiltersAppliedIndicator = useMemo(
        () => ({
            [ReportEnum.summaryReportDashboard]: !objectComparator(
                initialSummaryReportDashboardFilters,
                summaryFilters,
                validMillSummaryDefaultFields,
            ),
            [ReportEnum.detailsReport]: !objectComparator(
                initialDetailsReportFilters,
                filters,
                validMillDetailsReportDefaultFields,
            ),
        }),
        [summaryFilters, filters],
    );

    const showDetailsButton = useMemo(
        () => Boolean(report === ReportEnum.liveDowntimeReport) && !isFullscreenActive,
        [report, isFullscreenActive],
    );

    const showPendingSecondaryCodesButton = useMemo(
        () => Boolean(report === ReportEnum.secondaryCodeRequirements) && !isFullscreenActive,
        [report, isFullscreenActive],
    );

    const handleShowPendingCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        showOrHideOnlyPending(event.target.checked);
    };

    const handleExcelDownload = async () => {
        setIsLoadingExcel(true);
        setShowSnackbar(true);
        setSnackbarMessage(t("locations.mill_dashboards.snackbar_message_loading"));

        const startDateRange = getStartOfDateRange(reportRangeStartDate as Date);
        const endDateRange = getEndOfDateRange(reportRangeEndDate as Date);
        const filePlaceholderName = t("locations.mill_dashboards.excel_name_placeholder", {
            extension: `${moment.utc(startDateRange).format(getDateFormatByLanguage())}-${moment
                .utc(endDateRange)
                .format(getDateFormatByLanguage())}.xlsx`,
        });

        const filtersSelected = {
            millId: Number(millId),
            startDateRange,
            endDateRange,
            languageCode: i18n.language.slice(0, 2),
            ...(filters.machineCenter?.length! > 0 &&
                !filters.machineCenter?.includes(allElements) && {
                    machineCenterIds: filters.machineCenter as number[],
                }),
            ...(filters.productionLine?.length! > 0 &&
                !filters.productionLine?.includes(allElements) && {
                    productionLineIds: filters.productionLine as number[],
                }),
            ...(filters.failureTypes?.length! > 0 &&
                !filters.failureTypes?.includes(allElements) && {
                    failureTypeIds: filters.failureTypes as number[],
                }),
            ...(filters.failureCodes?.length! > 0 &&
                !filters.failureCodes?.includes(allElements) && {
                    failureCodeIds: filters.failureCodes as number[],
                }),
            ...(filters.functionalLocation !== allElements && {
                functionalLocationId: filters.functionalLocation as number,
            }),
            ...(filters.shift !== allElements && {
                shiftId: filters.shift as number,
            }),
            ...(filters.operator !== allElements && {
                operatorId: filters.operator as number,
            }),
        };

        try {
            await getEventReportDetailsExcelFile(filtersSelected, filePlaceholderName);
        } catch {
            setSnackbarSeverity("error");
            setSnackbarMessage(t("locations.mill_dashboards.snackbar_message_error"));
        }
        setIsLoadingExcel(false);
    };

    const isOverLimitRange = useMemo(
        () => moment(reportRangeEndDate).diff(moment(reportRangeStartDate), "days") >= maxExcelGenerationRange,
        [reportRangeEndDate, reportRangeStartDate],
    );

    const connectivityWarning = useMemo(() => {
        if (isDisconnected) {
            return (
                <Tooltip
                    color="warning"
                    placement="top-start"
                    PopperProps={{
                        className: classes.tooltip,
                    }}
                    title={
                        <Typography className={classes.tooltipTexts}>
                            {t("locations.mill_dashboards.local_server_connection_warning")}
                        </Typography>
                    }
                >
                    <Alert
                        sx={{ cursor: "pointer", height: "72px" }}
                        className={classes.alertInfo}
                        severity="warning"
                        onClick={() =>
                            navigate(`#${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.HASH_PATHS.LOCAL_SERVER}`, {
                                replace: true,
                            })
                        }
                        color="warning"
                        icon={<PermScanWifiIcon className={classes.warningIcon} />}
                    >
                        {t("locations.mill_network_details.disconnection_message")}
                    </Alert>
                </Tooltip>
            );
        }
    }, [isDisconnected, t, classes, navigate]);

    return (
        <div>
            <div className={classes.header}>
                {!isFullscreenActive && (
                    <Box className={classes.dropdownBox}>
                        <div className={classes.reportSelectWrapper}>
                            <Typography variant="subtitle2" className={classes.inputLabel}>
                                {t("locations.mill_dashboards.report_dropdown.select_report").toUpperCase()}
                                {CORPORATE_PERMISSIONS?.corporationPermissions?.showSecondaryCodeRequirements && (
                                    <Tooltip
                                        placement="right-end"
                                        PopperProps={{
                                            className: classes.tooltip,
                                        }}
                                        title={
                                            <Typography className={classes.tooltipTexts}>
                                                {t("locations.mill_dashboards.secondary_code_requirements_tooltip")}
                                            </Typography>
                                        }
                                    >
                                        <Badge
                                            badgeContent={pendingEventReportsCount}
                                            color="error"
                                            className={classes.badgeTitle}
                                        />
                                    </Tooltip>
                                )}
                            </Typography>
                            <Select
                                IconComponent={KeyboardArrowDownIcon}
                                className={classes.select}
                                value={report}
                                onChange={(event) => {
                                    setReport(event.target.value as ReportEnum);
                                    if (report === ReportEnum.liveDowntimeReport) {
                                        handleShift(ShiftOptionsEnum.current);
                                    }
                                }}
                            >
                                {menuOptionComponents}
                            </Select>
                        </div>
                        {showPendingSecondaryCodesButton && (
                            <Alert
                                sx={{ width: "40%", alignSelf: "start", height: "72px" }}
                                className={classes.alertInfo}
                                severity="info"
                            >
                                {t("locations.mill_dashboards.secondary_code_requirements_info")}
                            </Alert>
                        )}
                        {report === ReportEnum.liveDowntimeReport && (
                            <ShiftDropdown shift={shift} handleShift={handleShift} />
                        )}
                        {!showReportDateRange && connectivityWarning}
                    </Box>
                )}
                {showReportDateRange && (
                    <div className={classes.reportDateRangeWrapper}>
                        <DateRange
                            className={classes.reportDateRange}
                            placeholderText={t("locations.summary_report.filters.select_date_range")}
                            startDate={seletedDateRange.startDate}
                            endDate={seletedDateRange.endDate}
                            onChange={(update) => {
                                const [start, end] = update as [Date, Date];
                                setSelectedDateRange({ startDate: start, endDate: end });

                                if (start && end) {
                                    handleSetReportRangeStartDate(start);
                                    handleSetReportRangeEndDate(end);
                                }
                            }}
                            label={t("locations.summary_report.filters.select_date_range")}
                        />
                    </div>
                )}
                {(report === ReportEnum.summaryReportDashboard || report === ReportEnum.trendReport) && (
                    <div className={classes.reportDisplayInWrapper}>
                        <div className={classes.reportSelectWrapper}>
                            <Typography variant="subtitle2" className={classes.inputLabel}>
                                {t("locations.summary_report.filters.display_in").toUpperCase()}
                            </Typography>
                            <Select
                                IconComponent={KeyboardArrowDownIcon}
                                className={classes.select}
                                value={displayUnit}
                                onChange={(event) => {
                                    handleChangeDisplayUnit(event.target.value);
                                }}
                            >
                                {displayInOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id} className={classes.menuItem}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    </div>
                )}
                {!showFiltersBySelectedReport[report as keyof typeof showFiltersBySelectedReport] &&
                    showFiltersAppliedIndicator[report as keyof typeof showFiltersAppliedIndicator] && (
                        <Alert
                            sx={{ alignSelf: "start", height: "72px" }}
                            className={classes.alertInfo}
                            severity="info"
                        >
                            {t("locations.mill_dashboards.filters_applied_alert")}
                        </Alert>
                    )}
                {showReportDateRange && connectivityWarning}
                <div className={classes.topHeaderArea}>{topHeaderArea}</div>
                <div className={isFullscreenActive ? classes.headerActionsFullscreen : classes.headerActions}>
                    {showDetailsButton && (
                        <div className={classes.headerActionWrapper} onClick={() => showOrHideDetails(!showDetails)}>
                            {showDetails ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            {showDetails
                                ? t("locations.mill_dashboards.hide_details")
                                : t("locations.mill_dashboards.show_details")}
                        </div>
                    )}
                    {showPendingSecondaryCodesButton && (
                        <div className={classes.headerActionWrapper}>
                            <FormControlLabel
                                className={classes.checkboxLabel}
                                control={
                                    <Checkbox
                                        checked={showOnlyPending}
                                        onChange={handleShowPendingCheckboxChange}
                                        inputProps={{ "aria-label": "controlled" }}
                                        className={classes.checkbox}
                                    />
                                }
                                label={t("locations.secondary_code_requirements_table.hide_completed")}
                            />
                        </div>
                    )}
                    {report === ReportEnum.detailsReport &&
                        (isOverLimitRange ? (
                            <Tooltip
                                placement="top"
                                PopperProps={{
                                    className: classes.tooltip,
                                }}
                                title={
                                    <Typography className={classes.tooltipTexts}>
                                        {t("locations.mill_dashboards.excel_download_limit_tooltip", {
                                            limit: maxExcelGenerationRange,
                                        })}
                                    </Typography>
                                }
                            >
                                <div
                                    style={{
                                        cursor: isLoadingExcel ? "wait" : isOverLimitRange ? "default" : "pointer",
                                        ...(isLoadingExcel || isOverLimitRange ? { color: "#9E9E9E" } : {}),
                                    }}
                                    onClick={isLoadingExcel || isOverLimitRange ? undefined : handleExcelDownload}
                                    className={classes.headerActionWrapper}
                                >
                                    <DownloadOutlinedIcon />
                                    {t("corporate_dashboard.export_xls")}
                                </div>
                            </Tooltip>
                        ) : (
                            <div
                                style={{
                                    cursor: isLoadingExcel ? "wait" : isOverLimitRange ? "default" : "pointer",
                                    ...(isLoadingExcel || isOverLimitRange ? { color: "#9E9E9E" } : {}),
                                }}
                                onClick={isLoadingExcel || isOverLimitRange ? undefined : handleExcelDownload}
                                className={classes.headerActionWrapper}
                            >
                                <DownloadOutlinedIcon />
                                {t("corporate_dashboard.export_xls")}
                            </div>
                        ))}
                    <FullScreenButton
                        isFullscreenActive={isFullscreenActive}
                        handleSetIsFullscreenActive={handleSetIsFullscreenActive}
                    />
                    {/* TODO: enable print button when functionality is ready */}
                    {report === ReportEnum.summaryReportDashboard && (
                        <div className={classes.headerActionWrapper} style={{ display: "none" }}>
                            <ToggleButtonGroup
                                color="info"
                                className={classes.headerFilterButton}
                                value={""}
                                exclusive
                                onChange={() => null}
                            >
                                <ToggleButton disabled value="print">
                                    <PrintIcon />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    )}
                    {showFiltersButton && (
                        <FiltersButton
                            isFilterApplied={
                                showFiltersAppliedIndicator[report as keyof typeof showFiltersAppliedIndicator]
                            }
                            showFilters={
                                showFiltersBySelectedReport[report as keyof typeof showFiltersBySelectedReport]
                            }
                            handleShowFiltersChange={
                                handleFiltersChangeBySelectedReport[
                                    report as keyof typeof handleFiltersChangeBySelectedReport
                                ]
                            }
                        />
                    )}
                </div>
            </div>
            <div className={classes.reportWrapper}>
                {
                    // @ts-ignore
                    reportsComponents[report].component
                }
            </div>
            <Snackbar
                open={showSnackbar}
                onClose={() => {
                    setShowSnackbar(false);
                    setSnackbarMessage("");
                    setSnackbarSeverity("info");
                }}
                autoHideDuration={isLoadingExcel ? null : 6000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default MillDashboard;
